import { Plus, Trash2 } from 'lucide-react';
import {
  TRAINING_TEXT_INPUT_KINDS,
  type TextGroupLayout,
  type TrainingTextGroupDef,
  type TrainingTextInputKind,
} from '@hcc/types';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';
import { genId } from './moduleOps';
import { SortableList, SortableRow, DragHandle, reorder } from './SortableList';

type Row = TrainingTextGroupDef['rows'][number];

const INPUT_LABEL: Record<TrainingTextInputKind, string> = {
  text: 'Text',
  date: 'Date',
  time: 'Time',
  datetime: 'Date and time',
};

const LAYOUT_OPTIONS: { value: TextGroupLayout; label: string }[] = [
  { value: 'stacked', label: 'Answer box under each question' },
  { value: 'inline', label: 'Answer box beside each question' },
];

/**
 * Editor for a "Questions and answers" page: a heading, an optional intro,
 * and one free-text question per row. Only offered in an unmarked
 * assessment, so there is no answer key and no points column; each row just
 * decides whether it wants a large box and whether it may be left blank.
 */
export function TextGroupEditor({
  group,
  onChange,
}: {
  group: TrainingTextGroupDef;
  onChange: (next: TrainingTextGroupDef) => void;
}) {
  function setRow(id: string, patch: Partial<Row>) {
    onChange({ ...group, rows: group.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Page heading</span>
        <input
          value={group.title ?? ''}
          onChange={(e) => onChange({ ...group, title: e.target.value })}
          placeholder="e.g. About your health"
          className={SHARED_INPUT_CLASS}
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Intro (optional)</span>
        <textarea
          rows={2}
          value={group.intro ?? ''}
          onChange={(e) => onChange({ ...group, intro: e.target.value })}
          placeholder="A line of context above the questions"
          className={SHARED_INPUT_CLASS}
        />
      </label>

      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Layout</span>
        <select
          value={group.layout ?? 'stacked'}
          onChange={(e) => onChange({ ...group, layout: e.target.value as TextGroupLayout })}
          className={SHARED_INPUT_CLASS}
        >
          {LAYOUT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-fg-muted">Questions</legend>
        <SortableList
          ids={group.rows.map((r) => r.id)}
          onReorder={(from, to) => onChange({ ...group, rows: reorder(group.rows, from, to) })}
          className="space-y-2"
        >
          {group.rows.map((row) => (
            <SortableRow
              key={row.id}
              id={row.id}
              className={(dragging) =>
                `rounded-token-sm border border-line/10 bg-surface p-2 ${
                  dragging ? 'shadow-token' : ''
                }`
              }
            >
              {({ dragHandle }) => (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <DragHandle dragHandle={dragHandle} label={`Reorder: ${row.prompt}`} />
                    <input
                      value={row.prompt}
                      onChange={(e) => setRow(row.id, { prompt: e.target.value })}
                      placeholder="e.g. Describe any medication you take regularly"
                      className={`${SHARED_INPUT_CLASS} mt-0 min-w-48 flex-1`}
                    />
                    <button
                      type="button"
                      aria-label="Remove question"
                      disabled={group.rows.length <= 1}
                      onClick={() =>
                        onChange({ ...group, rows: group.rows.filter((r) => r.id !== row.id) })
                      }
                      className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap items-end gap-3 pl-7">
                    <label className="text-xs">
                      <span className="font-medium text-fg-muted">Answer type</span>
                      <select
                        value={row.input ?? 'text'}
                        onChange={(e) =>
                          setRow(row.id, { input: e.target.value as TrainingTextInputKind })
                        }
                        className={`${SHARED_INPUT_CLASS} text-xs`}
                      >
                        {TRAINING_TEXT_INPUT_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {INPUT_LABEL[k]}
                          </option>
                        ))}
                      </select>
                    </label>
                    {(row.input ?? 'text') === 'text' ? (
                      <label className="flex items-center gap-1.5 pb-2 text-xs text-fg-muted">
                        <input
                          type="checkbox"
                          checked={row.multiline === true}
                          onChange={(e) => setRow(row.id, { multiline: e.target.checked })}
                        />
                        Large comments box
                      </label>
                    ) : null}
                    <label className="flex items-center gap-1.5 pb-2 text-xs text-fg-muted">
                      <input
                        type="checkbox"
                        checked={row.optional === true}
                        onChange={(e) => setRow(row.id, { optional: e.target.checked })}
                      />
                      Optional
                    </label>
                    {(row.input ?? 'text') === 'text' ? (
                      <label className="min-w-48 flex-1 text-xs">
                        <span className="font-medium text-fg-muted">Placeholder (optional)</span>
                        <input
                          value={row.placeholder ?? ''}
                          onChange={(e) => setRow(row.id, { placeholder: e.target.value })}
                          placeholder="e.g. Leave blank if none"
                          className={`${SHARED_INPUT_CLASS} text-xs`}
                        />
                      </label>
                    ) : null}
                  </div>
                </div>
              )}
            </SortableRow>
          ))}
        </SortableList>
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() =>
            onChange({
              ...group,
              rows: [...group.rows, { id: genId('q'), prompt: '', points: 0 }],
            })
          }
        >
          Add question
        </Button>
      </fieldset>
    </div>
  );
}
